import type { Moment } from 'moment';
import type {
  AppointmentStatus,
  EnhancedAppointment,
} from '@bb/domain/dist/types';
import { AVAILABLE_APPOINTMENT_STATUSES } from '@bb/domain/dist/types';
import type { Listing } from 'api/ListingController/getListing';
import type { ShowingCalendarMenuOption } from './types';
import { AgentCalendarMenu, ListingCalendarMenu } from './types';

export const covidNeedsAction = ({
  covid19Overridden,
  covid19RequestedAck,
  covid19Signed,
  status,
  timeEnd,
}: Pick<
  EnhancedAppointment,
  | 'status'
  | 'covid19Overridden'
  | 'covid19RequestedAck'
  | 'covid19Signed'
  | 'timeEnd'
>) =>
  new Date(timeEnd) > new Date() &&
  covid19RequestedAck &&
  !covid19Signed &&
  !covid19Overridden &&
  AVAILABLE_APPOINTMENT_STATUSES.includes(status);

export const timeChangeNeedsAction = ({
  isBuyer,
  listingContactTimeChange,
  status,
}: Pick<EnhancedAppointment, 'status' | 'listingContactTimeChange'> & {
  isBuyer: boolean;
}) => status === 'time_change' && isBuyer === listingContactTimeChange;

export const buyerNeedsAction = ({
  covid19Overridden,
  covid19RequestedAck,
  covid19Signed,
  listingContactTimeChange,
  status,
  timeEnd,
}: Pick<
  EnhancedAppointment,
  | 'status'
  | 'listingContactTimeChange'
  | 'covid19Overridden'
  | 'covid19RequestedAck'
  | 'covid19Signed'
  | 'timeEnd'
>) =>
  timeChangeNeedsAction({ isBuyer: true, listingContactTimeChange, status }) ||
  covidNeedsAction({
    covid19RequestedAck,
    covid19Overridden,
    covid19Signed,
    status,
    timeEnd,
  });

export const listingAgentNeedsAction = ({
  listingContactTimeChange,
  status,
}: Pick<EnhancedAppointment, 'status' | 'listingContactTimeChange'>) =>
  ['new', 'attempted'].includes(status) ||
  timeChangeNeedsAction({ isBuyer: false, listingContactTimeChange, status });

export const getPreferenceSearchString = (
  preferences?: AppointmentStatus[],
) => {
  if (preferences) {
    return `&${preferences.map(pref => `preferences=${pref}`).join('&')}`;
  }
  return '';
};

export const getDateSearchString = (date?: [Moment, Moment]) => {
  let searchString = '';
  const [dateFrom, dateTo] = date || [];
  if (dateFrom) searchString += `&dateFrom=${dateFrom.toISOString()}`;
  if (dateTo) searchString += `&dateTo=${dateTo.toISOString()}`;
  return searchString;
};

export const isListingContact = (
  userId: string,
  { reps, hiddenReps }: Pick<Listing, 'reps' | 'hiddenReps'>,
) => (
    reps.some(({ id }) => id === userId) ||
    hiddenReps.some(({ id }) => id === userId)
  );

export const getPrintLink = (
  menuType: ShowingCalendarMenuOption,
  options: {
    date: string;
    preferences?: AppointmentStatus[];
    listingId?: string;
  },
) => {
  const { date, listingId, preferences } = options;
  // The same date value is sent for dateFrom and dateTo to generate a daily report
  switch (menuType) {
    case AgentCalendarMenu.Buyer:
      return `/get_appointments_instructions#?date=${date}`;
    case AgentCalendarMenu.Listing:
      return `/appointment/report/downloadPDF?group=UserDetails&dateFrom=${date}&dateTo=${date}${getPreferenceSearchString(
        preferences,
      )}&sortAsc=true&sortField=time`;
    case ListingCalendarMenu.InPerson:
    case ListingCalendarMenu.Virtual:
      return `/appointment/report/downloadPDF?group=ListingDetails&listingId=${listingId}&dateFrom=${date}&dateTo=${date}&sortAsc=true&sortField=time`;

    default:
      return `/invalidLink`;
  }
};
